import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Home.css';

function Home() {
  const [instagramMedia, setInstagramMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);
  const { t } = useTranslation();
  const accessToken = process.env.REACT_APP_INSTAGRAM_ACCESS_TOKEN;

  useEffect(() => {
    document.title = 'Sapium Beauty | Skin Treatments East London & Laser Hair Removal Central London';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional skin treatments in East London (28 Barking Rd, E16 1EQ) and laser hair removal in Central London (398 St John St, EC1V 4NJ). Book your free consultation today. Advanced Dermatology laser Razorlase SDL-L technology for safe, effective treatments.');
    }
  }, []);

  const featuredServices = [
    'Facial Treatments',
    'Chemical Peels',
    'Microneedling',
    'Dermaplaning',
    'Skin Booster',
    'Polynucleotides',
    'Laser Hair Removal'
  ];

  useEffect(() => {
    const fetchInstagramMedia = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!accessToken) {
          console.error("Instagram access token is missing in Home component!");
          throw new Error("Instagram access token is missing");
        }
        
        console.log("Home: Fetching Instagram media with token:", accessToken ? `${accessToken.substring(0, 10)}...` : 'missing');
        
        const response = await fetch(
          `https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,thumbnail_url&access_token=${accessToken}`
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Home: Instagram API error:", response.status, errorData);
          throw new Error(`Instagram API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
        }
        
        const data = await response.json();
        
        if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
          const limitedMedia = data.data.slice(0, 5);
          console.log(`Home: Successfully loaded ${limitedMedia.length} Instagram posts`);
          setInstagramMedia(limitedMedia);
        } else {
          console.error("Home: No media found or error in response:", data);
          setError("No Instagram posts found");
        }
      } catch (error) {
        console.error("Home: Error fetching Instagram media:", error);
        setError("Unable to load Instagram feed");
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramMedia();
  }, [accessToken]);

  // Auto-rotating gallery effect
  useEffect(() => {
    const scrollSpeed = 1; // pixels per interval
    const scrollDelay = 30; // ms between scrolls

    function startScrolling() {
      if (!carouselRef.current) return;
      
      // Clear any existing interval first
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(() => {
        // Check if carousel still exists and has required properties before accessing
        const carousel = carouselRef.current;
        if (!carousel || 
            typeof carousel.scrollLeft === 'undefined' || 
            typeof carousel.scrollWidth === 'undefined' || 
            typeof carousel.clientWidth === 'undefined') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }
        
        try {
          const scrollLeft = carousel.scrollLeft;
          const scrollWidth = carousel.scrollWidth;
          const clientWidth = carousel.clientWidth;
          
          if (scrollLeft >= scrollWidth - clientWidth) {
            carousel.scrollLeft = 0;
          } else {
            carousel.scrollLeft += scrollSpeed;
          }
        } catch (error) {
          // If there's an error, stop the interval
          console.error('Carousel scroll error:', error);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, scrollDelay);
    }

    function stopScrolling() {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Wait a bit for the carousel to be fully rendered
    const timer = setTimeout(() => {
      const carousel = carouselRef.current;
      if (carousel && 
          typeof carousel.scrollLeft !== 'undefined' && 
          typeof carousel.scrollWidth !== 'undefined' && 
          typeof carousel.clientWidth !== 'undefined') {
        carousel.addEventListener('mouseenter', stopScrolling);
        carousel.addEventListener('mouseleave', startScrolling);
        startScrolling();
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      stopScrolling();
      const carousel = carouselRef.current;
      if (carousel) {
        carousel.removeEventListener('mouseenter', stopScrolling);
        carousel.removeEventListener('mouseleave', startScrolling);
      }
    };
  }, []);

  // Fallback images if Instagram feed fails to load
  const fallbackImages = [
    {
      type: 'image',
      src: '/image/Iryna.jpg',
      alt: t('home.about.specialist')
    },
    {
      type: 'image',
      src: '/image/laser_hair_removal_background.jpg',
      alt: 'Modern beauty and wellness equipment'
    },
    {
      type: 'image',
      src: '/image/Logo4.jpg',
      alt: 'Sapium Beauty professional logo'
    }
  ];

  const handleVideoError = (e) => {
    console.error('Video failed to load:', e.target.src);
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>{t('home.hero.title')}</h1>
          <h2>{t('home.hero.subtitle')}</h2>
          <p>{t('home.hero.description')}</p>
          <a href="https://widget.treatwell.co.uk/place/beauty-lounge-green-canning-town/" target="_blank" rel="noopener noreferrer" className="cta-button">{t('home.hero.cta')}</a>
        </div>
      </section>

      {/* About Us Section */}
      <section className="about">
        <div className="container">
          <h2>{t('home.about.title')}</h2>
          <p>{t('home.about.description')}</p>
          <Link to="/contact" className="secondary-button">{t('home.about.findUs')}</Link>
        </div>
      </section>

      {/* Services Section */}
      <section className="services">
        <div className="container">
          <h2>{t('home.services.title')}</h2>
          <p>{t('home.services.description')}</p>
          <div className="services-grid">
            <div className="service-category">
              <h3>{t('home.services.featured')}</h3>
              <ul>
                {featuredServices.map((service, index) => (
                  <li key={index}>{service}</li>
                ))}
              </ul>
            </div>
            <div className="service-category">
              <h3>{t('home.services.areas')}</h3>
              <ul>
                <li>{t('home.services.categories.face')}</li>
                <li>{t('home.services.categories.body')}</li>
                <li>{t('home.services.categories.intimate')}</li>
                <li>{t('home.services.customPlans')}</li>
                <li>{t('home.services.consultation')}</li>
              </ul>
            </div>
          </div>
          <Link to="/services" className="cta-button">{t('home.services.viewAll')}</Link>
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section className="gallery-preview">
        <div className="container">
          <h2>{t('home.gallery.title')}</h2>
          <div className="gallery-carousel" ref={carouselRef}>
            <div className="carousel-track">
              {loading ? (
                <div className="loading" aria-live="polite">{t('gallery.loading')}</div>
              ) : error ? (
                // Show fallback images if Instagram feed fails
                [...fallbackImages, ...fallbackImages].map((media, index) => (
                  <div key={index} className="carousel-slide">
                    <img 
                      src={media.src} 
                      alt={media.alt} 
                      className="carousel-media"
                      onError={(e) => {
                        console.error('Image failed to load:', e.target.src);
                        e.target.src = '/image/Logo4.jpg';
                      }}
                    />
                  </div>
                ))
              ) : instagramMedia && instagramMedia.length > 0 ? (
                // Show Instagram media
                [...instagramMedia, ...instagramMedia].map((media, index) => (
                  <div key={`${media.id}-${index}`} className="carousel-slide">
                    {media.media_type === 'VIDEO' ? (
                      <video 
                        className="carousel-media"
                        controls
                        playsInline
                        poster={media.thumbnail_url}
                        onError={handleVideoError}
                      >
                        <source src={media.media_url} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img 
                        src={media.media_url} 
                        alt={media.caption || 'Instagram post'} 
                        className="carousel-media"
                        onError={(e) => {
                          console.error('Image failed to load:', e.target.src);
                          e.target.src = '/image/Logo4.jpg';
                        }}
                      />
                    )}
                  </div>
                ))
              ) : (
                // Fallback if no Instagram media
                [...fallbackImages, ...fallbackImages].map((media, index) => (
                  <div key={index} className="carousel-slide">
                    <img 
                      src={media.src} 
                      alt={media.alt} 
                      className="carousel-media"
                      onError={(e) => {
                        console.error('Image failed to load:', e.target.src);
                        e.target.src = '/image/Logo4.jpg';
                      }}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
          <Link to="/gallery" className="secondary-button">{t('home.gallery.viewFull')}</Link>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-preview">
        <div className="container">
          <h2>{t('home.contact.visitUs')}</h2>
          <div className="contact-info">
            <p><strong>{t('contact.mainTreatmentsTitle')}:</strong> {t('contact.mainTreatmentsAddress')}</p>
            <p><strong>{t('contact.laserHairRemovalTitle')}:</strong> {t('contact.laserHairRemovalAddress')}</p>
            <p>{t('home.contact.callUs')}: {t('contact.phone')}</p>
            <p>Email: {t('contact.email')}</p>
            <div className="social-links">
              <a 
                href="https://www.instagram.com/iryna_laser_glow/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
              >
                Instagram
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61567286369269" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
              >
                Facebook
              </a>
            </div>
          </div>
          <Link to="/contact" className="cta-button">{t('home.contact.contactUs')}</Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
